/**
 * writeAheadLog.ts — Journal (Write-Ahead Log / redo-log) para atomicidad
 * multi-clave y durabilidad ante interrupciones.
 *
 * QUÉ HACE:
 *  - Antes de aplicar un conjunto de escrituras (una transacción), se guarda un
 *    "journal" con TODOS los valores finales en UNA sola clave. Esa escritura es
 *    atómica a nivel de backend (un único setItem).
 *  - Si la app se cierra/crashea mientras se aplican las escrituras clave por
 *    clave, al reiniciar `recoverPendingTransactions()` reaplica el journal
 *    completo (redo idempotente) y luego lo limpia. Nunca queda un estado parcial.
 *
 * POR QUÉ FUNCIONA (garantía todo-o-nada):
 *  - Escritura del journal incompleta → no se aplicó ninguna clave → consistente.
 *  - Journal escrito + crash a mitad de aplicar → boot reaplica todo → consistente.
 *  - Todo aplicado + journal sin limpiar por crash → boot reaplica (idempotente) → OK.
 *
 * QUÉ EXPORTA: JournalWrite, Journal, writeJournal, clearJournal, readJournal,
 *              recoverPendingTransactions.
 *
 * QUÉ NO DEBE HACER:
 *  - No conocer la tecnología concreta (recibe siempre un StorageBackend).
 *  - No contener lógica de negocio.
 */

import type { StorageBackend } from "./storageBackend";

/** Clave reservada donde vive el journal. No usar para datos de negocio. */
export const JOURNAL_KEY = "__altenot_wal__";

/** Una escritura pendiente dentro de una transacción. */
export interface JournalWrite {
  /** Clave de almacenamiento afectada. */
  key: string;
  /** Valor final completo a persistir (ignorado si `remove` es true). */
  value: unknown;
  /** Si true, la clave debe eliminarse en lugar de escribirse. */
  remove: boolean;
  /**
   * Si true, el valor es un string escalar que debe persistirse en formato
   * crudo (`setRaw`), sin serialización JSON. Para banderas/punteros legados
   * que otros módulos leen con `localStorage.getItem` directo.
   */
  raw?: boolean;
  /**
   * Cadena FINAL ya serializada (plana o `lz1:…`) de `value`. La escribe el
   * motor de transacciones para no serializar el mismo valor dos veces
   * (journal + clave). Si no viene (journals de versiones anteriores), la
   * reaplicación serializa `value` con `set`.
   */
  serialized?: string;
}

/** Registro atómico de una transacción antes de aplicarse. */
export interface Journal {
  txId: string;
  createdAt: string;
  writes: JournalWrite[];
}

/**
 * Persiste el journal (un único set atómico) antes de aplicar las escrituras.
 * Los valores viajan ya serializados (`JournalWrite.serialized`); el journal se
 * guarda como string plano (JSON) SIN re-comprimir, para no pagar una segunda
 * compresión síncrona de todos los datos en cada commit.
 */
export function writeJournal(backend: StorageBackend, journal: Journal): void {
  backend.setSerialized(JOURNAL_KEY, JSON.stringify(journal), journal);
}

/** Elimina el journal una vez aplicadas todas las escrituras. */
export function clearJournal(backend: StorageBackend): void {
  backend.remove(JOURNAL_KEY);
}

/** Lee el journal pendiente, o null si no hay ninguno. */
export function readJournal(backend: StorageBackend): Journal | null {
  return backend.get<Journal | null>(JOURNAL_KEY, null);
}

/**
 * Reaplica cualquier transacción incompleta encontrada al arrancar la app.
 * Idempotente: los valores del journal son completos, reaplicarlos deja el
 * estado exactamente como debía quedar tras el commit.
 *
 * @returns número de escrituras reaplicadas (0 si no había journal pendiente).
 */
export function recoverPendingTransactions(backend: StorageBackend): number {
  const journal = readJournal(backend);
  if (!journal || !Array.isArray(journal.writes)) {
    clearJournal(backend);
    return 0;
  }

  for (const write of journal.writes) {
    // Journal nuevo: `serialized` ya es la cadena final (no re-serializar).
    // Journal legacy: sin `serialized`, se persiste `value` como antes.
    if (write.remove) {
      backend.remove(write.key);
    } else if (write.serialized != null) {
      if (write.raw) {
        backend.setRaw(write.key, write.serialized);
      } else {
        backend.setSerialized(write.key, write.serialized, write.value);
      }
    } else if (write.raw) {
      backend.setRaw(write.key, String(write.value));
    } else {
      backend.set(write.key, write.value);
    }
  }

  clearJournal(backend);
  return journal.writes.length;
}
